import { GeckoCase } from 'types'
import { parseDate, parseString } from 'utils/parsingHelpers'
import { z } from 'zod'

import { decodeCaseField } from './caseFieldCodec'
import { normalizeMdCaseEditableNoteContent } from './mdCaseNotes'

export const MD_CASE_WORKFLOW_DOMAIN = 'agentic-md' as const

export const MD_CASE_STATUSES = [
  'New Alert',
  'Open',
  'Resolved',
  'Closed',
  'Cleared',
  'Shelved',
] as const

export const MD_CASE_WORKFLOW_MODES = ['watch', 'investigate'] as const
export const MD_CASE_SOURCE_TYPES = ['manual', 'alert'] as const
export const MD_CASE_SEVERITIES = ['High', 'Medium', 'Low'] as const
export const MD_CASE_PROMOTION_STATES = ['roc_only', 'sent_to_site'] as const
const WORKFLOW_TIMESTAMP_FIELDS = [
  'published_at',
  'opened_at',
  'resolved_at',
  'closed_at',
  'cleared_at',
  'shelved_at',
] as const
const WORKFLOW_USER_FIELDS = [
  'published_by',
  'opened_by_user_id',
  'resolved_by_user_id',
  'closed_by_user_id',
  'cleared_by_user_id',
  'shelved_by_user_id',
] as const
const NOTE_FIELDS = [
  'working_notes',
  'recommendation_draft',
  'published_recommendation',
  'recommendation_summary',
] as const
const ALERT_TIMESTAMP_FIELDS = [
  'start_at',
  'end_at',
  'focus_start_at',
  'focus_end_at',
] as const
const ALERT_STRING_FIELDS = ['pi_tag_id', 'title'] as const
const PI_TAG_REFERENCE_STRING_FIELDS = [
  'pi_tag_id',
  'datapoint_entry_id',
  'chart_id',
  'added_by',
] as const
const PI_TAG_REFERENCE_TIMESTAMP_FIELDS = [
  'start_at',
  'end_at',
  'added_at',
] as const
const FEEDBACK_SECTION_KEYS = [
  'overall',
  'references',
  'investigation_quality',
  'recommendation_accuracy',
] as const

const NON_EMPTY_STRING_MESSAGE = 'Expected a non-empty string'
const TIMESTAMP_MESSAGE = 'Expected a valid timestamp'
const STRING_VALUE_MESSAGE = 'Expected a string value'

const NullableStringSchema = z.string().trim().min(1).nullable()
const TimestampStringSchema = z
  .string()
  .refine((value) => Boolean(parseDate(value)))
const NullableOutputStringSchema = z.string().nullable()
const RequiredOutputStringSchema = z.string()

const createSchemaShape = <
  Keys extends readonly string[],
  Schema extends z.ZodTypeAny,
>(
  keys: Keys,
  schema: Schema
) =>
  Object.fromEntries(keys.map((key) => [key, schema])) as Record<
    Keys[number],
    Schema
  >

export const MdCaseMetaSchema = z.object({
  title: NullableOutputStringSchema,
  workflow_mode: z.enum(MD_CASE_WORKFLOW_MODES).nullable(),
  source_type: z.enum(MD_CASE_SOURCE_TYPES).nullable(),
})

export const MdCaseWorkflowSchema = z.object({
  case_status: z.enum(MD_CASE_STATUSES).nullable(),
  severity: z.enum(MD_CASE_SEVERITIES).nullable(),
  promotion_state: z.enum(MD_CASE_PROMOTION_STATES).nullable(),
  ...createSchemaShape(WORKFLOW_TIMESTAMP_FIELDS, NullableOutputStringSchema),
  ...createSchemaShape(WORKFLOW_USER_FIELDS, NullableOutputStringSchema),
  reopen_reason: NullableOutputStringSchema,
})

export const MdCaseAlertItemSchema = z.object({
  id: z.string(),
  ...createSchemaShape(ALERT_STRING_FIELDS, NullableOutputStringSchema),
  pi_tag_ids: z.array(z.string()),
  ...createSchemaShape(ALERT_TIMESTAMP_FIELDS, NullableOutputStringSchema),
  summary: RequiredOutputStringSchema,
  severity: z.enum(MD_CASE_SEVERITIES).nullable(),
})

export const MdCasePiTagReferenceSchema = z.object({
  id: z.string(),
  ...createSchemaShape(
    PI_TAG_REFERENCE_STRING_FIELDS,
    NullableOutputStringSchema
  ),
  ...createSchemaShape(
    PI_TAG_REFERENCE_TIMESTAMP_FIELDS,
    NullableOutputStringSchema
  ),
})

export const MdCaseAlertsSchema = z.object({
  alert_ids: z.array(z.string()),
  items: z.array(MdCaseAlertItemSchema).optional(),
})

export const MdCaseReferencesSchema = z.object({
  pi_tag: NullableOutputStringSchema,
  documents: z.array(z.string()),
  pi_tag_items: z.array(MdCasePiTagReferenceSchema).optional(),
})

export const MdCaseAnnotationsSchema = z.object({
  items: z.array(z.string()),
})

export const MdCaseNotesSchema = z.object({
  ...createSchemaShape(NOTE_FIELDS, RequiredOutputStringSchema),
})

export const MdCaseFeedbackSectionSchema = z.object({
  rating: NullableOutputStringSchema,
  comment: RequiredOutputStringSchema,
})

export const MdCaseFeedbackSchema = z.object({
  ...createSchemaShape(FEEDBACK_SECTION_KEYS, MdCaseFeedbackSectionSchema),
  updated_at: NullableOutputStringSchema,
  updated_by: NullableOutputStringSchema,
})

export const MdCaseAgentSchema = z.object({
  thread_id: NullableOutputStringSchema,
  feedback: MdCaseFeedbackSchema.nullable(),
  // Free-form rationale text persisted by the build's recommendation
  // phase (Phase B). Read by the case-inquiry chat agent so "why
  // High?" / "why this title?" questions cite the actual reasoning.
  // Optional to stay backwards-compatible with pre-Phase-B cases.
  recommendation_rationale: z.string().nullable().optional(),
})

export type MdCaseStatus = (typeof MD_CASE_STATUSES)[number]
export type MdCaseWorkflowMode = (typeof MD_CASE_WORKFLOW_MODES)[number]
export type MdCaseSourceType = (typeof MD_CASE_SOURCE_TYPES)[number]
export type MdCaseSeverity = (typeof MD_CASE_SEVERITIES)[number]
export type MdCasePromotionState = (typeof MD_CASE_PROMOTION_STATES)[number]

export type MdCaseMeta = z.infer<typeof MdCaseMetaSchema>
export type MdCaseWorkflow = z.infer<typeof MdCaseWorkflowSchema>
export type MdCaseAlertItem = z.infer<typeof MdCaseAlertItemSchema>
export type MdCasePiTagReference = z.infer<typeof MdCasePiTagReferenceSchema>
export type MdCaseAlerts = z.infer<typeof MdCaseAlertsSchema>
export type MdCaseReferences = z.infer<typeof MdCaseReferencesSchema>
export type MdCaseAnnotations = z.infer<typeof MdCaseAnnotationsSchema>
export type MdCaseNotes = z.infer<typeof MdCaseNotesSchema>
export type MdCaseFeedbackSection = z.infer<typeof MdCaseFeedbackSectionSchema>
export type MdCaseFeedback = z.infer<typeof MdCaseFeedbackSchema>
export type MdCaseFeedbackSections = Pick<
  MdCaseFeedback,
  'overall' | 'references' | 'investigation_quality' | 'recommendation_accuracy'
>
export type MdCaseAgent = z.infer<typeof MdCaseAgentSchema>

export interface MdCase {
  id: string
  siteId: string | null
  organizationId: string | null
  workflowDomain: typeof MD_CASE_WORKFLOW_DOMAIN
  rawCase: GeckoCase
  meta: MdCaseMeta
  workflow: MdCaseWorkflow
  alerts: MdCaseAlerts
  references: MdCaseReferences
  annotations: MdCaseAnnotations
  notes: MdCaseNotes
  agent: MdCaseAgent
}

export interface MdCaseNormalizationError {
  path: string
  message: string
}

export interface MdCaseNormalizationResult {
  isMdCase: boolean
  hasCoreFields: boolean
  isValid: boolean
  errors: MdCaseNormalizationError[]
  value: MdCase | null
}

export interface MdCaseTransitionValidationInput {
  from: MdCaseStatus
  to: MdCaseStatus
  reopenReason?: string | null
}

export interface MdCaseTransitionValidationResult {
  isValid: boolean
  requiresReopenReason: boolean
  normalizedReopenReason: string | null
  error: string | null
}

type RawCaseData = Record<string, unknown>
type RawCaseTags = Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const getNestedRecord = (
  source: Record<string, unknown>,
  key: string
): Record<string, unknown> => (isRecord(source[key]) ? source[key] : {})

const getRawCaseData = (caseEntry: GeckoCase): RawCaseData =>
  isRecord(caseEntry.raw.data) ? caseEntry.raw.data : {}

const getRawCaseTags = (caseEntry: GeckoCase): RawCaseTags =>
  isRecord(caseEntry.raw.tags) ? caseEntry.raw.tags : {}

const pushError = (
  errors: MdCaseNormalizationError[],
  path: string,
  message: string
) => {
  errors.push({ path, message })
}

const formatIssuePath = (
  basePath: string,
  issuePath: readonly (string | number)[]
): string => {
  let nextPath = basePath
  issuePath.forEach((part) => {
    nextPath =
      typeof part === 'number' ? `${nextPath}[${part}]` : `${nextPath}.${part}`
  })
  return nextPath
}

const appendSchemaIssues = (
  errors: MdCaseNormalizationError[],
  basePath: string,
  issues: readonly { path: readonly (string | number)[]; message: string }[]
) => {
  issues.forEach((issue) => {
    pushError(errors, formatIssuePath(basePath, issue.path), issue.message)
  })
}

const parseNullableString = (
  value: unknown,
  errors: MdCaseNormalizationError[],
  path: string,
  message: string = NON_EMPTY_STRING_MESSAGE
): string | null => {
  if (value === undefined || value === null) return null
  const result = NullableStringSchema.safeParse(value)
  if (result.success) return result.data
  pushError(errors, path, message)
  return null
}

const parseNullableTimestamp = (
  value: unknown,
  errors: MdCaseNormalizationError[],
  path: string
): string | null => {
  if (value === undefined || value === null) return null
  const result = TimestampStringSchema.safeParse(value)
  if (result.success) return result.data
  pushError(errors, path, TIMESTAMP_MESSAGE)
  return null
}

const parseOptionalText = (
  value: unknown,
  errors: MdCaseNormalizationError[],
  path: string
): string => {
  if (value === undefined || value === null) return ''
  const parsed = parseString(value)
  if (parsed !== undefined) return parsed
  pushError(errors, path, STRING_VALUE_MESSAGE)
  return ''
}

const parseEnumValue = <T extends readonly string[]>(
  allowedValues: T,
  value: unknown,
  errors: MdCaseNormalizationError[],
  path: string,
  message: string
): T[number] | null => {
  if (value === undefined || value === null) return null
  const parsedValue = parseString(value)
  if (parsedValue && allowedValues.includes(parsedValue)) {
    return parsedValue as T[number]
  }
  pushError(errors, path, message)
  return null
}

const parseStringArray = (
  value: unknown,
  path: string,
  errors: MdCaseNormalizationError[]
): string[] => {
  if (value === undefined || value === null) return []
  if (!Array.isArray(value)) {
    pushError(errors, path, 'Expected an array of strings')
    return []
  }

  return value.flatMap((item, index) => {
    const result = z.string().safeParse(item)
    if (result.success) return [result.data]
    appendSchemaIssues(errors, `${path}[${index}]`, [
      { path: [], message: STRING_VALUE_MESSAGE },
    ])
    return []
  })
}

const parseRecordArray = <Item>(
  value: unknown,
  path: string,
  errors: MdCaseNormalizationError[],
  normalizeItem: (
    item: Record<string, unknown>,
    itemPath: string,
    errors: MdCaseNormalizationError[]
  ) => Item | null
): Item[] => {
  if (value === undefined || value === null) return []
  if (!Array.isArray(value)) {
    pushError(errors, path, 'Expected an array of objects')
    return []
  }

  return value.flatMap((item, index) => {
    const itemPath = `${path}[${index}]`
    if (!isRecord(item)) {
      pushError(errors, itemPath, 'Expected an object value')
      return []
    }
    const normalizedItem = normalizeItem(item, itemPath, errors)
    return normalizedItem ? [normalizedItem] : []
  })
}

const mergeStringArrays = (...valueSets: readonly string[][]): string[] => {
  const mergedValues: string[] = []
  const seenValues = new Set<string>()

  valueSets.forEach((values) => {
    values.forEach((value) => {
      if (!seenValues.has(value)) {
        seenValues.add(value)
        mergedValues.push(value)
      }
    })
  })

  return mergedValues
}

const normalizeNullableStringFields = <Keys extends readonly string[]>(
  source: Record<string, unknown>,
  keys: Keys,
  errors: MdCaseNormalizationError[],
  pathPrefix: string
): Record<Keys[number], string | null> =>
  Object.fromEntries(
    keys.map((key) => [
      key,
      parseNullableString(source[key], errors, `${pathPrefix}.${key}`),
    ])
  ) as Record<Keys[number], string | null>

const normalizeNullableTimestampFields = <Keys extends readonly string[]>(
  source: Record<string, unknown>,
  keys: Keys,
  errors: MdCaseNormalizationError[],
  pathPrefix: string
): Record<Keys[number], string | null> =>
  Object.fromEntries(
    keys.map((key) => [
      key,
      parseNullableTimestamp(source[key], errors, `${pathPrefix}.${key}`),
    ])
  ) as Record<Keys[number], string | null>

const normalizeMeta = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseMeta => {
  const meta = getNestedRecord(rawData, 'meta')
  const canonicalTitle = parseNullableString(meta.title, errors, 'meta.title')
  const title =
    canonicalTitle ?? parseString(rawData.case_title)?.trim() ?? null
  if (title === null || title.length === 0) {
    pushError(errors, 'meta.title', 'Missing M&D case title')
  }

  return MdCaseMetaSchema.parse({
    title: title && title.length ? title : null,
    workflow_mode: parseEnumValue(
      MD_CASE_WORKFLOW_MODES,
      meta.workflow_mode,
      errors,
      'meta.workflow_mode',
      'Expected a valid M&D workflow mode'
    ),
    source_type: parseEnumValue(
      MD_CASE_SOURCE_TYPES,
      meta.source_type,
      errors,
      'meta.source_type',
      'Expected a valid M&D source type'
    ),
  })
}

const normalizeWorkflow = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseWorkflow => {
  const workflow = getNestedRecord(rawData, 'workflow')
  const meta = getNestedRecord(rawData, 'meta')
  const caseStatus =
    parseEnumValue(
      MD_CASE_STATUSES,
      workflow.case_status,
      errors,
      'workflow.case_status',
      'Expected a valid M&D case status'
    ) ??
    parseEnumValue(
      MD_CASE_STATUSES,
      rawData.case_status,
      [],
      'workflow.case_status',
      'Expected a valid M&D case status'
    )

  if (caseStatus === null) {
    pushError(errors, 'workflow.case_status', 'Missing M&D case status')
  }

  return MdCaseWorkflowSchema.parse({
    case_status: caseStatus,
    severity:
      parseEnumValue(
        MD_CASE_SEVERITIES,
        workflow.severity,
        errors,
        'workflow.severity',
        'Expected a valid M&D severity'
      ) ??
      parseEnumValue(
        MD_CASE_SEVERITIES,
        meta.severity,
        [],
        'workflow.severity',
        'Expected a valid M&D severity'
      ),
    promotion_state: parseEnumValue(
      MD_CASE_PROMOTION_STATES,
      workflow.promotion_state,
      errors,
      'workflow.promotion_state',
      'Expected a valid M&D promotion state'
    ),
    ...normalizeNullableTimestampFields(
      workflow,
      WORKFLOW_TIMESTAMP_FIELDS,
      errors,
      'workflow'
    ),
    ...normalizeNullableStringFields(
      workflow,
      WORKFLOW_USER_FIELDS,
      errors,
      'workflow'
    ),
    reopen_reason: parseNullableString(
      workflow.reopen_reason,
      errors,
      'workflow.reopen_reason'
    ),
  })
}

const normalizeAlertItem = (
  value: Record<string, unknown>,
  path: string,
  errors: MdCaseNormalizationError[]
): MdCaseAlertItem | null => {
  const id = parseNullableString(value.id, errors, `${path}.id`)
  if (id === null) return null

  const piTagId = parseNullableString(
    value.pi_tag_id,
    errors,
    `${path}.pi_tag_id`
  )
  return MdCaseAlertItemSchema.parse({
    id,
    pi_tag_id: piTagId,
    pi_tag_ids: mergeStringArrays(
      piTagId ? [piTagId] : [],
      parseStringArray(value.pi_tag_ids, `${path}.pi_tag_ids`, errors)
    ),
    ...normalizeNullableTimestampFields(
      value,
      ALERT_TIMESTAMP_FIELDS,
      errors,
      path
    ),
    summary: parseOptionalText(value.summary, errors, `${path}.summary`),
    title: parseNullableString(value.title, errors, `${path}.title`),
    severity: parseEnumValue(
      MD_CASE_SEVERITIES,
      value.severity,
      errors,
      `${path}.severity`,
      'Expected a valid M&D severity'
    ),
  })
}

const normalizePiTagReferenceItem = (
  value: Record<string, unknown>,
  path: string,
  errors: MdCaseNormalizationError[]
): MdCasePiTagReference | null => {
  const id = parseNullableString(value.id, errors, `${path}.id`)
  if (id === null) return null

  return MdCasePiTagReferenceSchema.parse({
    id,
    ...normalizeNullableStringFields(
      value,
      PI_TAG_REFERENCE_STRING_FIELDS,
      errors,
      path
    ),
    ...normalizeNullableTimestampFields(
      value,
      PI_TAG_REFERENCE_TIMESTAMP_FIELDS,
      errors,
      path
    ),
  })
}

const normalizeAlerts = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseAlerts => {
  const alerts = getNestedRecord(rawData, 'alerts')
  const items = parseRecordArray(
    alerts.items,
    'alerts.items',
    errors,
    normalizeAlertItem
  )

  return MdCaseAlertsSchema.parse({
    alert_ids: mergeStringArrays(
      parseStringArray(alerts.alert_ids, 'alerts.alert_ids', errors),
      items.map((item) => item.id)
    ),
    items,
  })
}

const normalizeReferences = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseReferences => {
  const references = getNestedRecord(rawData, 'references')

  return MdCaseReferencesSchema.parse({
    pi_tag: parseNullableString(references.pi_tag, errors, 'references.pi_tag'),
    documents: parseStringArray(
      references.documents,
      'references.documents',
      errors
    ),
    pi_tag_items: parseRecordArray(
      references.pi_tag_items,
      'references.pi_tag_items',
      errors,
      normalizePiTagReferenceItem
    ),
  })
}

const normalizeAnnotations = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseAnnotations => {
  const annotations = getNestedRecord(rawData, 'annotations')

  return MdCaseAnnotationsSchema.parse({
    items: parseStringArray(annotations.items, 'annotations.items', errors),
  })
}

/**
 * Note fields that are stored base64-encoded in `*_b64` siblings on the
 * Rolodex entry.  See `caseFieldCodec` — Cloud Armor's WAF blocks PATCH
 * bodies containing literal `<script>` / `<img` / `<svg` / `javascript:`
 * substrings, so the LLM-text fields go through base64 in flight.
 */
type B64EncodedNoteField =
  | 'working_notes'
  | 'recommendation_draft'
  | 'published_recommendation'

const decodeNoteField = (
  rawNotes: Record<string, unknown>,
  field: B64EncodedNoteField,
  errors: MdCaseNormalizationError[]
): string => {
  const encoded = rawNotes[`${field}_b64`]
  if (encoded === undefined || encoded === null) return ''
  if (typeof encoded !== 'string') {
    pushError(errors, `notes.${field}_b64`, STRING_VALUE_MESSAGE)
    return ''
  }
  return decodeCaseField(encoded)
}

const normalizeNotes = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseNotes => {
  const rawNotes = getNestedRecord(rawData, 'notes')

  // Decode the three b64-stored LLM text fields back into plain HTML
  // for the in-memory MdCase shape.  The legacy plain `working_notes` /
  // `recommendation_draft` / `published_recommendation` keys in storage
  // are no longer read.
  const workingNotes = decodeNoteField(rawNotes, 'working_notes', errors)
  const recommendationDraft = decodeNoteField(
    rawNotes,
    'recommendation_draft',
    errors
  )
  const publishedRecommendation = decodeNoteField(
    rawNotes,
    'published_recommendation',
    errors
  )

  // `recommendation_summary` is not base64-encoded; it is short plain
  // text built from the recommendation draft and never carries HTML.
  const summary = parseOptionalText(
    rawNotes.recommendation_summary,
    errors,
    'notes.recommendation_summary'
  )

  return MdCaseNotesSchema.parse({
    working_notes: normalizeMdCaseEditableNoteContent(workingNotes),
    recommendation_draft:
      normalizeMdCaseEditableNoteContent(recommendationDraft),
    published_recommendation: normalizeMdCaseEditableNoteContent(
      publishedRecommendation
    ),
    recommendation_summary: summary,
  })
}

export const createEmptyMdCaseFeedbackSection = (): MdCaseFeedbackSection =>
  MdCaseFeedbackSectionSchema.parse({
    rating: null,
    comment: '',
  })

export const createEmptyMdCaseFeedback = (): MdCaseFeedback =>
  MdCaseFeedbackSchema.parse({
    ...Object.fromEntries(
      FEEDBACK_SECTION_KEYS.map((sectionKey) => [
        sectionKey,
        createEmptyMdCaseFeedbackSection(),
      ])
    ),
    updated_at: null,
    updated_by: null,
  })

const normalizeFeedbackSection = (
  sectionKey: keyof MdCaseFeedbackSections,
  rawFeedback: Record<string, unknown>,
  errors: MdCaseNormalizationError[]
): MdCaseFeedbackSection => {
  const rawSection =
    rawFeedback[sectionKey] && isRecord(rawFeedback[sectionKey])
      ? (rawFeedback[sectionKey] as Record<string, unknown>)
      : {}

  return MdCaseFeedbackSectionSchema.parse({
    rating: parseNullableString(
      rawSection.rating,
      errors,
      `agent.feedback.${sectionKey}.rating`
    ),
    comment: parseOptionalText(
      rawSection.comment,
      errors,
      `agent.feedback.${sectionKey}.comment`
    ),
  })
}

const normalizeFeedbackSections = (
  rawFeedback: Record<string, unknown>,
  errors: MdCaseNormalizationError[]
): MdCaseFeedbackSections =>
  Object.fromEntries(
    FEEDBACK_SECTION_KEYS.map((sectionKey) => [
      sectionKey,
      normalizeFeedbackSection(sectionKey, rawFeedback, errors),
    ])
  ) as MdCaseFeedbackSections

const normalizeAgent = (
  rawData: RawCaseData,
  errors: MdCaseNormalizationError[]
): MdCaseAgent => {
  const agent = getNestedRecord(rawData, 'agent')
  const rawFeedback =
    agent.feedback && isRecord(agent.feedback) ? agent.feedback : null

  return MdCaseAgentSchema.parse({
    thread_id: parseNullableString(agent.thread_id, errors, 'agent.thread_id'),
    feedback: rawFeedback
      ? {
          ...normalizeFeedbackSections(rawFeedback, errors),
          updated_at: parseNullableTimestamp(
            rawFeedback.updated_at,
            errors,
            'agent.feedback.updated_at'
          ),
          updated_by: parseNullableString(
            rawFeedback.updated_by,
            errors,
            'agent.feedback.updated_by'
          ),
        }
      : null,
    recommendation_rationale: parseNullableString(
      // Rationale is stored base64-encoded in `recommendation_rationale_b64`
      // alongside any legacy plain value (no longer read).  Decode the b64
      // field and surface it as a nullable plain string in the MdCase model.
      typeof agent.recommendation_rationale_b64 === 'string'
        ? decodeCaseField(agent.recommendation_rationale_b64) || null
        : null,
      errors,
      'agent.recommendation_rationale_b64'
    ),
  })
}

export const isMdCaseRecord = (
  caseEntry: GeckoCase | null | undefined
): boolean => {
  const rawTags = caseEntry?.raw.tags as Record<string, unknown> | undefined
  const workflowDomain = rawTags?.workflow_domain
  return workflowDomain === MD_CASE_WORKFLOW_DOMAIN
}

export const normalizeMdCaseRecord = (
  caseEntry: GeckoCase | null | undefined
): MdCaseNormalizationResult => {
  if (!caseEntry || !isMdCaseRecord(caseEntry)) {
    return {
      isMdCase: false,
      hasCoreFields: false,
      isValid: false,
      errors: [
        {
          path: 'tags.workflow_domain',
          message: 'Case is not an M&D case',
        },
      ],
      value: null,
    }
  }

  const rawData = getRawCaseData(caseEntry)
  const rawTags = getRawCaseTags(caseEntry)
  const errors: MdCaseNormalizationError[] = []

  const normalizedCase: MdCase = {
    id: caseEntry.id,
    siteId: parseString(rawTags.site)?.trim() || null,
    organizationId: parseString(rawTags.organization)?.trim() || null,
    workflowDomain: MD_CASE_WORKFLOW_DOMAIN,
    rawCase: caseEntry,
    meta: normalizeMeta(rawData, errors),
    workflow: normalizeWorkflow(rawData, errors),
    alerts: normalizeAlerts(rawData, errors),
    references: normalizeReferences(rawData, errors),
    annotations: normalizeAnnotations(rawData, errors),
    notes: normalizeNotes(rawData, errors),
    agent: normalizeAgent(rawData, errors),
  }

  const hasCoreFields =
    normalizedCase.meta.title !== null &&
    normalizedCase.workflow.case_status !== null

  return {
    isMdCase: true,
    hasCoreFields,
    isValid: hasCoreFields && errors.length === 0,
    errors,
    value: normalizedCase,
  }
}
